import { atom, useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback, useMemo } from "react";

import {
	$requestPageHistory,
	$selectRequestPageHistoryEntry,
	$selectedRequestPageHistoryId,
	type RequestPageHistoryEntry,
} from "../../internal/request-page";

const $emptyRequestPageHistory = atom(async () => [] as RequestPageHistoryEntry[]);
$emptyRequestPageHistory.debugPrivate = true;

const $emptySelectedRequestPageHistoryId = atom(null as number | null);
$emptySelectedRequestPageHistoryId.debugPrivate = true;

export const useRequestPageHistory = (documentId: string, pageId?: string | null) => {
	const params = useMemo(() => (pageId ? { documentId, pageId } : null), [documentId, pageId]);
	const historyAtom = useMemo(
		() => (params ? $requestPageHistory(params) : $emptyRequestPageHistory),
		[params]
	);
	const historyLoadable = useAtomValue(useMemo(() => loadable(historyAtom), [historyAtom]));
	const selectedEntryId = useAtomValue(
		useMemo(
			() =>
				params ? $selectedRequestPageHistoryId(params) : $emptySelectedRequestPageHistoryId,
			[params]
		)
	);
	const selectHistoryEntryAtom = useSetAtom($selectRequestPageHistoryEntry);

	const selectHistoryEntry = useCallback(
		async (historyEntryId: number) => {
			if (!pageId) {
				return;
			}

			await selectHistoryEntryAtom({ documentId, pageId, historyEntryId });
		},
		[documentId, pageId, selectHistoryEntryAtom]
	);

	return {
		entries: historyLoadable.state === "hasData" ? historyLoadable.data : [],
		selectedEntryId,
		selectHistoryEntry,
		isLoading: historyLoadable.state === "loading",
		isError: historyLoadable.state === "hasError",
		error: historyLoadable.state === "hasError" ? historyLoadable.error : null,
	};
};
